import React from 'react';
import styled from 'styled-components';
import { media } from '@peloton/styles';
import { white } from '@engage/colors';
import {
  toggle,
  sort,
  getCollectionFilters,
  getToggleFilters,
  getSortGroup,
  FilterProvider,
} from '@engage/filters';
import type { FilterValue, Selectable } from '@engage/filters';
import { getActiveCategorySlug } from '@engage/library';
import { useReduxState, useReduxAction } from '@engage/redux';
import { spaces } from '@engage/styles';
import CollectionFilter, { ToggleFilter, SortGroup } from './CollectionFilter';
import { TakenToggleFilter } from './ToggleFilter';

const FilterGroupListItem = styled.li`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  justify-content: space-between;
`;
const Wrapper = styled.div`
  background: ${white};
  border-radius: 5px;
  width: 100%;
  display: flex;
  padding: ${spaces.small}px;
`;

const FilterGroupsView: React.FC<React.PropsWithChildren<Props>> = ({ isFlyoutOpen }) => {
  const slug = useReduxState(getActiveCategorySlug);
  const collections = useReduxState(getCollectionFilters);
  const toggles = useReduxState(getToggleFilters);
  const sorts = useReduxState(getSortGroup);
  const toggleFilter = useReduxAction(toggle);
  const selectSort = useReduxAction(sort);
  const bookmarkToggle = toggles.find(t => t.name === 'is_favorite_ride');
  const freemiumToggle = toggles.find(t => t.name === 'app-free');
  const takenWorkoutToggle = toggles.find(t => t.name === 'has_workout');
  const hasCyclingArmsToggle = toggles.find(t => t.name === 'has_cycling_arms');

  const groupsExist = (listOfOptions: Selectable<FilterValue>[]) => {
    return (
      listOfOptions.length ===
      listOfOptions.filter((option: FilterValue) => option.group)?.length
    );
  };

  const areWeightsButtonsDisplayed = !!hasCyclingArmsToggle;

  return (
    <List>
      <FilterProvider isFlyoutOpen={isFlyoutOpen}>
        <>
          {!!freemiumToggle && slug !== 'free' && (
            <ToggleFilter
              key={freemiumToggle.name}
              filterGroup={freemiumToggle}
              toggleFilter={toggleFilter}
            />
          )}
          {!!bookmarkToggle && (
            <ToggleFilter
              key={bookmarkToggle.name}
              filterGroup={bookmarkToggle}
              toggleFilter={toggleFilter}
            />
          )}
          {(window.location.pathname.includes('all')
            ? collections.filter(
                x => x.name !== 'difficulty_level' && x.name !== 'class_type_id',
              )
            : collections
          ).map(filterGroup => (
            <CollectionFilter
              key={filterGroup.name}
              filterGroup={filterGroup}
              toggleFilter={toggleFilter}
              grouped={groupsExist(filterGroup.values)}
            />
          ))}
          <SortGroup sorts={sorts} selectSort={selectSort} />
          <FilterGroupListItem>
            <Wrapper>
              {!!takenWorkoutToggle && (
                <>
                  <TakenToggleFilter
                    key={takenWorkoutToggle.values[0].displayName}
                    filterGroup={takenWorkoutToggle!}
                    toggleFilter={toggleFilter}
                    order={0}
                  />
                  <TakenToggleFilter
                    key={takenWorkoutToggle.values[1].displayName}
                    filterGroup={takenWorkoutToggle!}
                    toggleFilter={toggleFilter}
                    order={1}
                  />
                </>
              )}
            </Wrapper>
          </FilterGroupListItem>
          {!!areWeightsButtonsDisplayed && (
            <FilterGroupListItem>
              <Wrapper>
                <TakenToggleFilter
                  key={hasCyclingArmsToggle.values[0].displayName}
                  filterGroup={hasCyclingArmsToggle!}
                  toggleFilter={toggleFilter}
                  order={0}
                />
                <TakenToggleFilter
                  key={hasCyclingArmsToggle.values[1].displayName}
                  filterGroup={hasCyclingArmsToggle!}
                  toggleFilter={toggleFilter}
                  order={1}
                />
              </Wrapper>
            </FilterGroupListItem>
          )}
        </>
      </FilterProvider>
    </List>
  );
};

const List = styled.ul`
  width: 100%;
  padding-top: ${spaces.xxxSmall}px;
  padding-bottom: ${spaces.large}px;
  min-height: 90vh;

  ${media.tablet`
    min-height: auto;
  `}
`;

type Props = { isFlyoutOpen: boolean };

export default FilterGroupsView;
